<template>
  <div ref="table_container" class="table-container mention-popper">
    <template v-if="tasks.length !== 0">
      <div v-if="isStandardScreen" class="table-total" :class="{ isScrolled }">
        <div class="total-num">{{ $t('owork_base.transmission.total_num', { count: tasks.length }) }}</div>
        <div class="total-clear" @click="onClickClearAll">{{ $t('owork_base.transmission.clear') }}</div>
      </div>
      <div class="table-body" :class="{ isScrolled }" v-bind="containerProps">
        <div class="transmission-wrap" v-bind="wrapperProps">
          <div
            v-for="item in list"
            :key="item.data.id"
            class="table-item"
            :class="{ active: item.data.id === taskId }"
            @mouseenter="onMouseenter"
          >
            <div class="transmission-item-hover"></div>
            <TransmissionItemComponent :task="item.data" :is-upload="isUpload" :is-desktop-env="isDesktopEnv" />
          </div>
        </div>
      </div>
    </template>
    <div v-else class="table-empty">
      <img
        class="empty-img"
        :class="{ isUpload: isUpload }"
        :src="isUpload ? transferNoUploads : transferNoDownloads"
        alt=""
      />
    </div>
    <a-modal
      :visible="isRevealed"
      :footer="null"
      centered
      width="440px"
      :get-container="() => ($refs.table_container as HTMLElement)"
      :mask-closable="false"
      :closable="isStandardScreen"
      @cancel="cancel"
    >
      <div class="mention-title">
        {{ $t('owork_base.transmission.clear_transmission_task_confirm_title', { transmissionType }) }}
      </div>

      <div class="mention-txt">
        {{
          isStandardScreen
            ? $t('owork_base.transmission.clear_transmission_task_tips', { transmissionType })
            : $t('owork_base.transmission.clear_transmission_task_confirm_message')
        }}
      </div>
      <div class="btn-box">
        <a-button class="confirm-button" :type="isStandardScreen ? 'primary' : 'link'" danger @click="confirm">{{
          $t('owork_base.transmission.clear')
        }}</a-button>
        <a-divider
          v-if="!isStandardScreen"
          type="vertical"
          style="height: 20px; background-color: rgba(0, 0, 0, 0.12)"
        />
        <a-button v-if="!isStandardScreen" type="link" class="button color-btn" @click="cancel">{{
          $t('owork_base.cancel')
        }}</a-button>
      </div>
    </a-modal>
  </div>
</template>
<script lang="ts" setup>
  import { ref, computed, onMounted, onBeforeUnmount, toRef } from 'vue'
  import transferNoDownloads from '@assets/svgs/preload-bg-transmission-download-list-empty.png'
  import transferNoUploads from '@assets/svgs/preload-bg-transmission-upload-list-empty.png'
  import type { TaskInfo } from '@/core/transmission/ITask'
  import TransmissionItemComponent from './TransmissionItemComponent.vue'
  import { INSTANCE as FileDownloadManager } from '@/core/transmission/FileDownloadManager'
  import { INSTANCE as FileUploadManager } from '@/core/transmission/FileUploadManager'
  import { useConfirmDialog, useVirtualList } from '@vueuse/core'
  import { useI18n } from 'vue-i18n'
  import { isDesktop } from '@/core/Utils'
  import { useSharedScreen } from '@/core/compositions/Screen'
  import { delay } from 'lodash-es'

  interface TransmissionProps {
    tasks: TaskInfo[]
    isUpload: boolean
    uncompletedTaskNum: number
    taskId?: string
  }
  const props = withDefaults(defineProps<TransmissionProps>(), {
    isUpload: true,
  })

  const { isStandardScreen, isLargeScreen } = useSharedScreen()
  const { t } = useI18n()

  const onMouseenter = (e: MouseEvent) => {
    ;(e.target as HTMLDivElement).classList.remove('active')
  }

  const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(toRef(props, 'tasks'), {
    itemHeight: isLargeScreen.value ? 44 : 64,
  })

  const transmissionType = computed(() =>
    props.isUpload
      ? t('owork_base.transmission.transmission_type_upload')
      : t('owork_base.transmission.transmission_type_download')
  )
  const { isRevealed, reveal, confirm, cancel } = useConfirmDialog()

  const onClickClearAll = async () => {
    // TODO: 所有确认框 抽离成一个通用组件
    if (props.uncompletedTaskNum > 0) {
      const { isCanceled } = await reveal()

      if (isCanceled) return
    }
    props.isUpload ? FileUploadManager.removeAllTask() : FileDownloadManager.removeAllTask()
  }

  const tableBodyScrollTop = ref(0)

  const onScroll = (e: Event) => {
    tableBodyScrollTop.value = (e.target as HTMLDivElement).scrollTop
  }
  const isScrolled = computed(() => tableBodyScrollTop.value > 0)
  const isDesktopEnv = ref<boolean>()

  const initDesktopEnv = async () => {
    isDesktopEnv.value = await isDesktop()
  }

  onMounted(() => {
    containerProps.ref.value?.addEventListener('scroll', onScroll)
    initDesktopEnv()

    if (props.taskId) {
      const itemIndex = props.tasks.findIndex((item) => item.id === props.taskId)

      delay(() => {
        scrollTo(itemIndex)
      }, 300)
    }
  })
  onBeforeUnmount(() => {
    containerProps.ref.value?.removeEventListener('scroll', onScroll)
  })

  defineExpose({
    onClickClearAll,
  })
</script>
<style scoped lang="stylus">
  @import '../styles/transfer.styl'

  ::-webkit-scrollbar
    width 6px
    border-radius 12px
    right 4px
  ::-webkit-scrollbar-track
    background-color #ffffff
  ::-webkit-scrollbar-thumb
    background-color #d9d9d9
  .table-container
    font-size 14px
    height 285px
    .table-total
      height 44px
      display flex
      justify-content space-between
      align-items center
      padding 0 8px 0 20px
      border-bottom 1px solid transparent
      box-sizing border-box
      &.isScrolled
        border-bottom 1px solid #eeeeee
      .total-num
        font-size 12px
        color default_shallow_color
      .total-clear
        width 48px
        height 28px
        color active_color
        cursor pointer
        text-align center
        line-height 28px
        border-radius 6px
        font-weight default_font_weight
        &:hover
        &:active
          background #00000014
    .table-body
      height 100%
      overflow-y auto
      scrollbar-color #d9d9d9
      box-sizing border-box
      padding-bottom 44px
      &.isScrolled
        border-top 0.33px solid rgba(0, 0, 0, 0.12)
      .table-item
        margin-bottom 4px
        position relative
        &:hover
          .transmission-item-hover
            background-color rgba(84, 86, 104, 0.04)
  .table-empty
    width 100%
    height 100%
    flex-all-center()
    .empty-img
      rect(280px,260px)
      margin-top 28px
  .mention-txt
    margin-top 28px + 26px
    padding 0 20px
    font-size 18px
    font-weight bolder_font_weight
    line-height 23px
    color default_color
    text-align center
  .btn-box
    margin 40px auto 26px
    flex-align-center(center)
  .confirm-button
    width 88px
    height 36px
    border-radius 8px
  .active
    .transmission-item-hover
      background-color rgba(84, 86, 104, 0.04)
  @import '../styles/h5Adaptive/Transmission.styl'
  @import '../styles/h5Adaptive/ConfirmDialog.styl'
</style>
